import type {
  ApiErrorBody,
  InstallationType,
  CreatedInstaller,
  DeviceRecord,
  Installer,
  IssuedCode,
  VehicleSearchResult,
  LoginResponse,
  PriceRow,
  Settlement,
  DeletedInstallation,
  InstallationFilter,
  InstallationPage,
} from './types';

/* Typed client for the admin API.
 *
 * One place that knows about headers, tokens and error shapes, so no screen has
 * to. Screens call methods and get either data or an ApiError.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The session is gone — the caller should send the user back to the login screen. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

export interface TokenStore {
  get(): string | null;
  set(token: string | null): void;
}

/**
 * Keeps the session token in sessionStorage, not localStorage: closing the tab
 * ends the session. This panel can issue subscriptions and approve money, and it
 * gets opened on whatever machine is to hand — a token that survives until
 * someone clears their browser is the wrong default for it.
 */
export const sessionTokenStore: TokenStore = {
  get: () => sessionStorage.getItem('outlander.admin.token'),
  set: (token) => {
    if (token === null) sessionStorage.removeItem('outlander.admin.token');
    else sessionStorage.setItem('outlander.admin.token', token);
  },
};

export class ApiClient {
  constructor(
    private readonly tokens: TokenStore,
    private readonly baseUrl = '',
    /**
     * Wrapped, not `= fetch`.
     *
     * `this.fetchImpl(...)` is a method call, so a bare `fetch` reference would
     * be invoked with the ApiClient as its `this`. The browser's fetch refuses
     * any receiver that is not the global object and throws `Illegal
     * invocation` — which lands in the catch below and is reported as "ارتباط
     * با سرور برقرار نشد", pointing at the network instead of at this line.
     *
     * Every test injects its own fetch, so this default is the one path the
     * suite can never cover.
     */
    private readonly fetchImpl: typeof fetch = (input, init) => globalThis.fetch(input, init),
  ) {}

  async login(username: string, password: string): Promise<LoginResponse> {
    const body = await this.request<LoginResponse>('POST', '/auth/admin/login', { username, password }, false);
    this.tokens.set(body.token);
    return body;
  }

  logout(): void {
    this.tokens.set(null);
  }

  get isLoggedIn(): boolean {
    return this.tokens.get() !== null;
  }

  /**
   * One box, any identifier: device code, serial, chassis number, the owner's
   * phone, or their name.
   *
   * It used to send whatever was typed as `serialno`, and the server derived a
   * device code from it — so searching for a device code answered with an
   * unrelated code for a car that does not exist.
   */
  lookup(query: string): Promise<VehicleSearchResult> {
    return this.request('POST', '/admin/lookup', { query });
  }

  /** Reads back the newest code for a customer who lost theirs. */
  recoverCode(serialno: string): Promise<IssuedCode> {
    return this.request('POST', '/admin/codes/recover', { serialno });
  }

  /**
   * Corrects a chassis number. The install flow writes it once and never again,
   * so this is the only way to fix a typo or fill in a car activated before the
   * field was mandatory. `null` clears it.
   */
  setVin(serialno: string, vin: string | null): Promise<DeviceRecord> {
    return this.request('POST', '/admin/devices/vin', { serialno, vin });
  }

  renew(serialno: string, planId = 0): Promise<IssuedCode> {
    return this.request('POST', '/admin/renewals', { serialno, planId });
  }

  customRenew(input: {
    serialno: string;
    note: string;
    mode: 'extend-by-days' | 'until-day' | 'days-from-today';
    days?: number;
    endDay?: number;
  }): Promise<IssuedCode> {
    return this.request('POST', '/admin/renewals/custom', input);
  }

  pendingSettlements(): Promise<Settlement[]> {
    return this.request('GET', '/admin/settlements/pending');
  }

  approveSettlement(id: number): Promise<Settlement> {
    return this.request('POST', `/admin/settlements/${id}/approve`, {});
  }

  rejectSettlement(id: number, reason: string): Promise<Settlement> {
    return this.request('POST', `/admin/settlements/${id}/reject`, { reason });
  }

  /* ---- prices: the one place the six numbers are set ---- */

  pricing(): Promise<PriceRow[]> {
    return this.request('GET', '/admin/pricing');
  }

  pricingHistory(): Promise<PriceRow[]> {
    return this.request('GET', '/admin/pricing/history');
  }

  /**
   * Replaces the whole list. All three operations go together — see
   * PricingService for why piecemeal edits are refused.
   */
  setPricing(
    prices: { type: InstallationType; customerPrice: string; installerShare: string }[],
    note?: string,
  ): Promise<PriceRow[]> {
    return this.request('PUT', '/admin/pricing', { prices, note });
  }

  /* ---- installer accounts (admin only) ---- */

  installers(): Promise<Installer[]> {
    return this.request('GET', '/admin/installers');
  }

  createInstaller(input: {
    username: string;
    fullName: string;
    phone?: string;
    password?: string;
  }): Promise<CreatedInstaller> {
    return this.request('POST', '/admin/installers', input);
  }

  updateInstaller(id: number, patch: { fullName?: string; phone?: string | null }): Promise<Installer> {
    return this.request('PATCH', `/admin/installers/${id}`, patch);
  }

  setInstallerActive(id: number, active: boolean): Promise<Installer> {
    return this.request('POST', `/admin/installers/${id}/active`, { active });
  }

  /** Returns the new password once. There is no way to read it back later. */
  setInstallerPassword(id: number, password?: string): Promise<{ password: string }> {
    return this.request('POST', `/admin/installers/${id}/password`, password ? { password } : {});
  }

  unlockInstaller(id: number): Promise<{ ok: true }> {
    return this.request('POST', `/admin/installers/${id}/unlock`, {});
  }

  /* ---- installations: what was done, by whom, and did it finish ---- */

  installations(filter: InstallationFilter = {}): Promise<InstallationPage> {
    const q = new URLSearchParams();
    /* Skip empty values rather than sending them: the server validates the
     * query, and `type=` with nothing after it is a validation error, not an
     * absent filter. */
    for (const [key, value] of Object.entries(filter)) {
      if (value === undefined || value === null || value === '') continue;
      q.set(key, String(value));
    }
    const query = q.toString();
    return this.request('GET', `/admin/installations${query ? `?${query}` : ''}`);
  }

  /**
   * Removes an installation so the vehicle can be done again from scratch.
   *
   * Destructive: on a first activation this takes the device, its subscription
   * and its codes with it. The reason is mandatory and ends up in the audit log,
   * which is the only thing that will explain it later.
   */
  deleteInstallation(id: number, reason: string): Promise<DeletedInstallation> {
    return this.request('DELETE', `/admin/installations/${id}`, { reason });
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    authenticated = true,
  ): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (authenticated) {
      const token = this.tokens.get();
      if (!token) throw new ApiError(401, 'وارد نشده‌اید');
      headers.authorization = `Bearer ${token}`;
    }

    let res: Response;
    try {
      res = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      /* A network failure is not a server error, and saying "خطای سرور" when the
       * VPN dropped sends the reader looking in the wrong place. */
      throw new ApiError(0, 'ارتباط با سرور برقرار نشد');
    }

    /* Only on an AUTHENTICATED call. On the login request itself a 401 means the
     * username or password was wrong, and the server says exactly that; treating
     * it as an expired session told the user to log in again on the screen they
     * were already logging in on, and hid the real reason. */
    if (res.status === 401 && authenticated) {
      /* Drop the dead token so the UI cannot keep retrying with it. */
      this.tokens.set(null);
      throw new ApiError(401, 'نشست شما منقضی شده است. دوباره وارد شوید.');
    }

    const text = await res.text();
    const parsed = text ? safeJson(text) : null;

    if (!res.ok) {
      const err = (parsed ?? {}) as ApiErrorBody;
      throw new ApiError(res.status, err.error ?? `خطای ${res.status}`, err.details);
    }
    return parsed as T;
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
